import { Icon } from '../../design-system/icons';
import { dsColors } from '../../design-system/tokens/colors';
import { CTA_GRADIENT } from './pricingStyles';
import {
  ANNUAL_DISCOUNT_LABEL,
  PRICING_CONTENT,
  PRICING_PLAN_KEYS,
  RECOMMENDED_PLAN,
  type PricingPlanKey,
} from './pricingContent';

/**
 * Controles del paywall rediseñado (Figma "Pricing", node 2044:448): el botón de
 * cerrar, el switch Mensual/Anual y el selector de plan. Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales.
 *
 * Los tres son "chrome": el cerrar y el switch van fijos sobre el hero y el CTA
 * va fijo al pie, así siguen accesibles mientras se scrollea el catálogo.
 */

/**
 * Botón circular flotante sobre el hero. Mismo tratamiento (vidrio oscuro) para
 * el de cerrar —arriba a la izquierda— y el de ayuda —arriba a la derecha—, así
 * se leen como un par.
 */
function GlassIconButton({
  icon,
  label,
  onClick,
}: {
  icon: 'outline/cross' | 'outline/whatsapp';
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex items-center justify-center rounded-full bg-black/50 p-2 backdrop-blur-[4px] transition-[transform,background-color] duration-200 active:scale-90 lg:hover:bg-black/70"
    >
      <Icon name={icon} size={20} style={{ color: dsColors.neutral[50] }} />
    </button>
  );
}

/**
 * Botón de cerrar. Dos tratamientos según sobre qué se apoya:
 *
 * - `glass` (default) — flotando sobre el hero en mobile: vidrio oscuro, porque
 *   detrás hay una foto y una cruz suelta se perdería.
 * - `plain` — dentro del panel blanco del modal de desktop: sin fondo y en
 *   `neutral-900`. Ahí no compite con ninguna imagen, y el disco oscuro pesaba
 *   más que la propia acción.
 */
export function CloseButton({
  onClose,
  variant = 'glass',
}: {
  onClose: () => void;
  variant?: 'glass' | 'plain';
}) {
  if (variant === 'plain') {
    return (
      <button
        type="button"
        onClick={onClose}
        aria-label="Cerrar"
        className="flex items-center justify-center rounded-full p-1.5 transition-[transform,background-color] duration-200 active:scale-90 hover:bg-black/[0.06]"
      >
        <Icon name="outline/cross" size={20} style={{ color: dsColors.neutral[900] }} />
      </button>
    );
  }
  return <GlassIconButton icon="outline/cross" label="Cerrar" onClick={onClose} />;
}

/** Botón circular de ayuda por WhatsApp, arriba a la derecha sobre el hero. */
export function HelpButton({ onHelp }: { onHelp: () => void }) {
  return (
    <GlassIconButton
      icon="outline/whatsapp"
      label="Escribinos por WhatsApp"
      onClick={onHelp}
    />
  );
}

/**
 * Switch Mensual / Anual. El plan anual **todavía no existe en el backend**: se
 * muestra sólo a cuentas `dev_tester` y por ahora es puramente visual (no cambia
 * precios ni el plan que se contrata). Ver ADR-2026-07-28-switch-mensual-anual-visual-solo.
 */
export function BillingToggle({
  annual,
  onChange,
  variant = 'onHero',
}: {
  annual: boolean;
  onChange: (annual: boolean) => void;
  /**
   * Mismo control en los dos casos —misma forma, misma tipografía, mismos
   * colores de texto—: lo único que cambia es que el vidrio se vuelve OPACO.
   *
   * `onHero` (mobile) va sobre una foto oscura, así que `black/40` compone casi
   * negro y el texto blanco se lee. `onPanel` (el panel blanco del modal de
   * desktop) componía gris medio (#999) y la etiqueta inactiva quedaba en ~2.1:1
   * — ilegible. Los opacos son los colores a los que el translúcido llega en
   * mobile, así que el control se ve igual en las dos plataformas (ADR-2026-07-30-switch-opaco-sobre-panel-blanco).
   */
  variant?: 'onHero' | 'onPanel';
}) {
  const panel = variant === 'onPanel';

  return (
    <div
      role="group"
      aria-label="Ciclo de facturación"
      className={`relative flex items-center rounded-full p-1 ${
        panel ? '' : 'bg-black/40 backdrop-blur-[4px]'
      }`}
      style={panel ? { background: dsColors.neutral[700] } : undefined}
    >
      {/* Pastilla activa: una sola que se DESLIZA entre las dos opciones, en vez
          de prenderse y apagarse el fondo de cada botón. El ancho sale del riel
          (total menos los 4px de padding por lado) y el desplazamiento va en % del
          propio ancho, no en px fijos → no depende del ancho de los botones.
          Va MÁS OSCURA que el riel, igual que en mobile. */}
      <span
        aria-hidden
        className={`absolute left-1 top-1 h-[calc(100%-8px)] rounded-full transition-transform duration-300 ease-out ${
          panel ? '' : 'bg-black/60'
        }`}
        style={{
          width: 'calc((100% - 0.5rem) / 2)',
          transform: `translateX(${annual ? 100 : 0}%)`,
          ...(panel ? { background: dsColors.neutral[900] } : null),
        }}
      />
      <button
        type="button"
        onClick={() => onChange(false)}
        aria-pressed={!annual}
        className={`relative z-10 flex w-[122px] items-center justify-center rounded-full py-2 text-[12px] font-[700] leading-[17px] transition-colors duration-200 ${
          annual ? 'text-white/70' : 'text-white'
        }`}
      >
        Mensual
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        aria-pressed={annual}
        className={`relative z-10 flex w-[122px] items-center justify-center gap-2 rounded-full py-2 text-[12px] font-[700] leading-[17px] transition-colors duration-200 ${
          annual ? 'text-white' : 'text-white/70'
        }`}
      >
        Anual
        <span
          className="rounded-lg px-1.5 py-0.5 text-[10px] font-[700] leading-[17px] text-white"
          style={{ background: 'var(--brand-primary)' }}
        >
          {ANNUAL_DISCOUNT_LABEL}
        </span>
      </button>
    </div>
  );
}

/**
 * Selector segmentado Básico / Pro / Ultra, sobre el sheet azul. El plan
 * recomendado lleva un badge que sobresale por encima del control.
 */
export function PlanSelector<K extends string = PricingPlanKey>({
  selected,
  onSelect,
  planKeys = PRICING_PLAN_KEYS as unknown as K[],
  items,
  variant = 'onSheet',
}: {
  selected: K;
  onSelect: (key: K) => void;
  planKeys?: K[];
  /**
   * Nombre y badge de cada pestaña. Sin esto, los de los planes individuales
   * (`PRICING_CONTENT[key].tab` y "Popular" en el recomendado). Los planes de equipo
   * pasan los suyos.
   */
  items?: { key: K; tab: string; badge?: string }[];
  /**
   * `onSheet` — sobre el sheet azul (mobile): riel translúcido blanco.
   * `onLight` — sobre el panel blanco del modal de desktop (ADR-2026-07-29-desktop-paywall-modal-dos-paneles): riel azul
   * suave y pastilla activa en azul de marca.
   */
  variant?: 'onSheet' | 'onLight';
}) {
  const activeIndex = Math.max(0, planKeys.indexOf(selected));
  const light = variant === 'onLight';
  const pestana = (key: K): { tab: string; badge?: string } =>
    items?.find((i) => i.key === key) ?? {
      tab: PRICING_CONTENT[key as unknown as PricingPlanKey].tab,
      badge: (key as string) === RECOMMENDED_PLAN ? 'Popular' : undefined,
    };

  return (
    <div
      role="tablist"
      aria-label="Planes"
      className={`relative flex w-full items-center rounded-full p-1 ${
        light ? '' : 'bg-white/30 backdrop-blur-[4px]'
      }`}
      style={light ? { background: 'rgb(var(--brand-primary-rgb) / 0.10)' } : undefined}
    >
      {/* Igual que el switch de facturación: una pastilla que se desliza. El ancho
          sale de dividir el riel (ancho total menos el padding de 4px por lado)
          entre la cantidad de planes, y el desplazamiento va en % del propio
          ancho → cae exacto sobre cada segmento sin medir el DOM. */}
      <span
        aria-hidden
        className={`absolute left-1 top-1 h-[calc(100%-8px)] rounded-full transition-transform duration-300 ease-out ${
          light ? '' : 'bg-white/80'
        }`}
        style={{
          width: `calc((100% - 0.5rem) / ${planKeys.length})`,
          transform: `translateX(${activeIndex * 100}%)`,
          ...(light ? { background: 'var(--brand-primary)' } : null),
        }}
      />
      {planKeys.map((key) => {
        const active = key === selected;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(key)}
            className="relative z-10 flex flex-1 items-center justify-center rounded-full py-2 text-[12px] font-[700] leading-[17px] transition-colors duration-200"
            style={{
              color: light
                ? active
                  ? '#fff'
                  : 'var(--brand-deep)'
                : active
                  ? 'var(--brand-black)'
                  : '#fff',
            }}
          >
            {pestana(key).tab}
            {pestana(key).badge ? (
              <span
                className="absolute -top-[18px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-[700] leading-[17px] text-white"
                style={{ background: CTA_GRADIENT }}
              >
                {pestana(key).badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** CTA principal (rosa degradado). Se usa fijo al pie en mobile y por card en desktop. */
export function SubscribeButton({
  label,
  onClick,
  disabled,
  loading,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className="flex w-full items-center justify-center gap-2 rounded-xl p-4 text-[16px] font-[700] leading-[21px] text-white transition-transform active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
      style={{ background: CTA_GRADIENT }}
    >
      {loading ? 'Procesando…' : label}
    </button>
  );
}
